/**
 * Minimap - shows overview of the game world
 * Enhanced with traditional Eastern aesthetic UI design
 * Features: territory colors, unit density heatmap, building markers, battle flashing
 */

import { FACTIONS } from '@/game/factions/FactionData';
import type { FactionId } from '@/types';
import type { TerritorySystem } from '@/game/systems/TerritorySystem';

interface MinimapUnit {
  x: number;
  z: number;
  factionId: FactionId;
}

interface MinimapBuilding {
  x: number;
  z: number;
  factionId: FactionId;
  isCapital: boolean;
}

interface BattleMarker {
  x: number;
  z: number;
  startTime: number;
  duration: number;
}

export class Minimap {
  private container: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private size: number = 260;
  private worldSize: number = 200; // Increased to show all factions
  private cameraX: number = 0;
  private cameraZ: number = 0;
  private cameraWidth: number = 40;
  private cameraHeight: number = 40;

  // Territory cache
  private territoryCanvas: HTMLCanvasElement;
  private territoryCtx: CanvasRenderingContext2D;
  private territoryCacheDirty: boolean = true;
  private lastCacheTime: number = 0;

  // Battle markers
  private battleMarkers: BattleMarker[] = [];

  // Home position (player's capital)
  private homeX: number = 0;
  private homeZ: number = 0;

  // Density grid
  private densityGrid: Map<string, { count: number; factionId: FactionId }> = new Map();
  private densityCellSize: number = 8;

  // Territory system for dynamic ownership
  private territorySystem: TerritorySystem | null = null;

  public onClick?: (worldX: number, worldZ: number) => void;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'minimap-container';
    this.container.innerHTML = `
      <style>
        #minimap-container {
          position: fixed;
          right: 16px;
          bottom: 50px;
          z-index: 10;
          user-select: none;
        }

        /* Container for minimap */
        .minimap-frame {
          position: relative;
          width: 260px;
          height: 260px;
          filter: drop-shadow(0 4px 12px rgba(0,0,0,0.6));
        }

        /* Canvas fills the container */
        #minimap {
          display: block;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        /* Frame overlay on top of canvas */
        .minimap-frame-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: url('/images/ui/decorations/minimap-frame.png');
          background-size: 115% 118%;
          background-position: center;
          background-repeat: no-repeat;
          pointer-events: none;
          z-index: 1;
        }

        /* Control buttons - positioned outside at the bottom */
        .minimap-controls {
          position: absolute;
          bottom: -36px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          justify-content: center;
          gap: 8px;
          z-index: 2;
        }

        .minimap-btn {
          width: 28px;
          height: 28px;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #8b6914;
          border-radius: 4px;
          color: #c4a77d;
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.4);
        }

        .minimap-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 100%);
          pointer-events: none;
        }

        .minimap-btn:hover {
          background: linear-gradient(180deg, #6b4e3a 0%, #4a3525 100%);
          border-color: #d4af37;
          color: #ffd35a;
        }

        .minimap-btn:active {
          transform: scale(0.95);
        }

        /* Compact layout for small viewports (desktop 720p) */
        @media (max-height: 800px) {
          #minimap-container {
            right: 10px;
            bottom: 34px;
          }
          .minimap-frame {
            width: 180px;
            height: 180px;
          }
          .minimap-controls {
            bottom: -26px;
            gap: 4px;
          }
          .minimap-btn {
            width: 22px;
            height: 22px;
            font-size: 11px;
          }
        }
      </style>

      <div class="minimap-frame">
        <canvas id="minimap"></canvas>
        <div class="minimap-frame-overlay"></div>

        <div class="minimap-controls">
          <button class="minimap-btn" id="minimap-zoom-in" title="放大">+</button>
          <button class="minimap-btn" id="minimap-zoom-out" title="缩小">−</button>
          <button class="minimap-btn" id="minimap-center" title="居中">⌂</button>
        </div>
      </div>
    `;

    // Get canvas from container
    document.body.appendChild(this.container);
    this.canvas = this.container.querySelector('#minimap')!;
    this.canvas.width = this.size;
    this.canvas.height = this.size;

    this.ctx = this.canvas.getContext('2d')!;

    // Create territory cache canvas
    this.territoryCanvas = document.createElement('canvas');
    this.territoryCanvas.width = this.size;
    this.territoryCanvas.height = this.size;
    this.territoryCtx = this.territoryCanvas.getContext('2d')!;

    this.setupEvents();
    this.renderTerritoryCache();
    this.render();
  }

  private setupEvents(): void {
    this.canvas.addEventListener('click', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const worldX = (x / this.size) * this.worldSize - this.worldSize / 2;
      const worldZ = (y / this.size) * this.worldSize - this.worldSize / 2;

      this.onClick?.(worldX, worldZ);
    });

    // Drag to move camera
    let isDragging = false;
    this.canvas.addEventListener('mousedown', () => {
      isDragging = true;
    });

    window.addEventListener('mouseup', () => {
      isDragging = false;
    });

    this.canvas.addEventListener('mousemove', (e) => {
      if (!isDragging) return;

      const rect = this.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const worldX = (x / this.size) * this.worldSize - this.worldSize / 2;
      const worldZ = (y / this.size) * this.worldSize - this.worldSize / 2;

      this.onClick?.(worldX, worldZ);
    });

    // Control buttons
    const zoomInBtn = this.container.querySelector('#minimap-zoom-in');
    const zoomOutBtn = this.container.querySelector('#minimap-zoom-out');
    const centerBtn = this.container.querySelector('#minimap-center');

    zoomInBtn?.addEventListener('click', () => {
      this.worldSize = Math.max(60, this.worldSize - 20);
      this.territoryCacheDirty = true;
    });

    zoomOutBtn?.addEventListener('click', () => {
      this.worldSize = Math.min(200, this.worldSize + 20);
      this.territoryCacheDirty = true;
    });

    centerBtn?.addEventListener('click', () => {
      this.onClick?.(this.homeX, this.homeZ);
    });
  }

  setWorldSize(size: number): void {
    this.worldSize = size;
    this.territoryCacheDirty = true;
  }

  setTerritorySystem(system: TerritorySystem): void {
    this.territorySystem = system;
    this.territoryCacheDirty = true;
  }

  setHomePosition(x: number, z: number): void {
    this.homeX = x;
    this.homeZ = z;
  }

  updateCamera(x: number, z: number, width: number, height: number): void {
    this.cameraX = x;
    this.cameraZ = z;
    this.cameraWidth = width;
    this.cameraHeight = height;
  }

  addBattleMarker(worldX: number, worldZ: number): void {
    // Avoid duplicate markers in same area
    const existing = this.battleMarkers.find(
      (m) => Math.abs(m.x - worldX) < 5 && Math.abs(m.z - worldZ) < 5 && Date.now() - m.startTime < 1000
    );
    if (existing) return;

    this.battleMarkers.push({
      x: worldX,
      z: worldZ,
      startTime: Date.now(),
      duration: 2000,
    });
  }

  private renderTerritoryCache(): void {
    const now = Date.now();
    // Update more frequently (every 500ms) since territory changes dynamically
    if (!this.territoryCacheDirty && now - this.lastCacheTime < 500) return;

    this.territoryCacheDirty = false;
    this.lastCacheTime = now;

    const ctx = this.territoryCtx;
    const size = this.size;
    const worldSize = this.worldSize;
    const resolution = 4; // Sample every 4 pixels

    // Neutral background color
    ctx.fillStyle = 'rgb(40, 35, 30)';
    ctx.fillRect(0, 0, size, size);

    // Sample territory at intervals using dynamic ownership
    for (let my = 0; my < size; my += resolution) {
      for (let mx = 0; mx < size; mx += resolution) {
        const wx = (mx / size) * worldSize - worldSize / 2;
        const wz = (my / size) * worldSize - worldSize / 2;

        // Get dynamic territory owner from TerritorySystem
        const owner = this.territorySystem?.getTileOwner(wx, wz);

        if (owner && owner !== 'neutral') {
          const faction = FACTIONS[owner];
          const color = faction.color;

          // Darken the color for the background
          const r = ((color >> 16) & 0xff) * 0.5;
          const g = ((color >> 8) & 0xff) * 0.5;
          const b = (color & 0xff) * 0.5;

          ctx.fillStyle = `rgb(${Math.floor(r)},${Math.floor(g)},${Math.floor(b)})`;
          ctx.fillRect(mx, my, resolution, resolution);
        }
      }
    }
  }

  render(units: MinimapUnit[] = [], buildings: MinimapBuilding[] = []): void {
    const ctx = this.ctx;
    const size = this.size;
    const worldSize = this.worldSize;
    const now = Date.now();

    // Update territory cache periodically
    this.renderTerritoryCache();

    // Draw territory background
    ctx.drawImage(this.territoryCanvas, 0, 0);

    // World to minimap conversion
    const toMinimapX = (wx: number) => ((wx + worldSize / 2) / worldSize) * size;
    const toMinimapY = (wz: number) => ((wz + worldSize / 2) / worldSize) * size;

    // Build density grid
    this.densityGrid.clear();
    for (const unit of units) {
      const cellX = Math.floor((unit.x + worldSize / 2) / this.densityCellSize);
      const cellZ = Math.floor((unit.z + worldSize / 2) / this.densityCellSize);
      const key = `${cellX},${cellZ}`;

      const existing = this.densityGrid.get(key);
      if (existing) {
        existing.count++;
      } else {
        this.densityGrid.set(key, { count: 1, factionId: unit.factionId });
      }
    }

    // Draw unit density as heatmap
    for (const [key, data] of this.densityGrid) {
      const [cellX, cellZ] = key.split(',').map(Number);
      const mx = (cellX * this.densityCellSize / worldSize) * size;
      const my = (cellZ * this.densityCellSize / worldSize) * size;
      const cellSize = (this.densityCellSize / worldSize) * size;

      const faction = FACTIONS[data.factionId];
      const intensity = Math.min(1, data.count / 10);
      const color = faction.color;
      const r = (color >> 16) & 0xff;
      const g = (color >> 8) & 0xff;
      const b = color & 0xff;

      ctx.fillStyle = `rgba(${r},${g},${b},${0.3 + intensity * 0.5})`;
      ctx.fillRect(mx, my, cellSize, cellSize);
    }

    // Draw buildings
    for (const building of buildings) {
      const mx = toMinimapX(building.x);
      const my = toMinimapY(building.z);
      const faction = FACTIONS[building.factionId];
      const colorHex = '#' + faction.color.toString(16).padStart(6, '0');

      if (building.isCapital) {
        // Draw capital as a larger icon with glow
        ctx.shadowColor = colorHex;
        ctx.shadowBlur = 8;
        ctx.fillStyle = colorHex;
        ctx.beginPath();
        ctx.arc(mx, my, 5, 0, Math.PI * 2);
        ctx.fill();

        // Gold border
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#ffd700';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Inner dot
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(mx, my, 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Draw regular building as small square
        ctx.shadowBlur = 0;
        ctx.fillStyle = colorHex;
        ctx.fillRect(mx - 2, my - 2, 4, 4);
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.lineWidth = 0.5;
        ctx.strokeRect(mx - 2, my - 2, 4, 4);
      }
    }

    ctx.shadowBlur = 0;

    // Draw battle markers with flashing effect
    const activeMarkers: BattleMarker[] = [];
    for (const marker of this.battleMarkers) {
      const elapsed = now - marker.startTime;
      if (elapsed < marker.duration) {
        activeMarkers.push(marker);

        const mx = toMinimapX(marker.x);
        const my = toMinimapY(marker.z);

        // Pulsing red circle
        const pulse = Math.sin(elapsed * 0.01) * 0.5 + 0.5;
        const radius = 4 + pulse * 4;
        const alpha = 0.8 - (elapsed / marker.duration) * 0.6;

        // Glow effect
        ctx.shadowColor = 'rgba(255, 50, 50, 0.8)';
        ctx.shadowBlur = 6;

        ctx.strokeStyle = `rgba(255, 50, 50, ${alpha})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(mx, my, radius, 0, Math.PI * 2);
        ctx.stroke();

        ctx.shadowBlur = 0;

        // Inner flash
        ctx.fillStyle = `rgba(255, 100, 100, ${alpha * 0.5})`;
        ctx.beginPath();
        ctx.arc(mx, my, radius * 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    this.battleMarkers = activeMarkers;

    // Draw camera viewport
    const camLeft = toMinimapX(this.cameraX - this.cameraWidth / 2);
    const camTop = toMinimapY(this.cameraZ - this.cameraHeight / 2);
    const camWidth = (this.cameraWidth / worldSize) * size;
    const camHeight = (this.cameraHeight / worldSize) * size;

    // Viewport with glow
    ctx.shadowColor = 'rgba(255, 255, 255, 0.5)';
    ctx.shadowBlur = 4;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 2;
    ctx.strokeRect(camLeft, camTop, camWidth, camHeight);
    ctx.shadowBlur = 0;

    // Corner markers (gold color)
    const cornerSize = 5;
    ctx.fillStyle = '#ffd700';
    ctx.fillRect(camLeft - 1, camTop - 1, cornerSize, 2);
    ctx.fillRect(camLeft - 1, camTop - 1, 2, cornerSize);
    ctx.fillRect(camLeft + camWidth - cornerSize + 1, camTop - 1, cornerSize, 2);
    ctx.fillRect(camLeft + camWidth - 1, camTop - 1, 2, cornerSize);
    ctx.fillRect(camLeft - 1, camTop + camHeight - 1, cornerSize, 2);
    ctx.fillRect(camLeft - 1, camTop + camHeight - cornerSize + 1, 2, cornerSize);
    ctx.fillRect(camLeft + camWidth - cornerSize + 1, camTop + camHeight - 1, cornerSize, 2);
    ctx.fillRect(camLeft + camWidth - 1, camTop + camHeight - cornerSize + 1, 2, cornerSize);
  }

  dispose(): void {
    this.container.remove();
  }
}
